import { __assign, __makeTemplateObject, __read } from "tslib";
/**
 * COMPONENTS AND LIBS
 */
import * as React from 'react';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import styled, { ThemeProvider } from 'styled-components';
import Container from './Tree/Container';
import coreTheme from './styles/theme';
import Icon from './Tree/Icon';
import Icons from './assets/images/Icons';
/**
 * Building blocks
 */
var DefaultLoaderIcon = Icons['loader'];
var TreeBoundary = styled(m.div)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  position: relative;\n  flex-direction: column;\n  ", ";\n  padding: 5px;\n  overflow-y: auto;\n  ", ";\n  color: ", ";\n  background-color: ", ";\n\n  & * {\n    user-select: none;\n    cursor: pointer;\n    box-sizing: border-box;\n    font-size: ", ";\n  }\n"], ["\n  display: flex;\n  position: relative;\n  flex-direction: column;\n  ", ";\n  padding: 5px;\n  overflow-y: auto;\n  ", ";\n  color: ", ";\n  background-color: ", ";\n\n  & * {\n    user-select: none;\n    cursor: pointer;\n    box-sizing: border-box;\n    font-size: ", ";\n  }\n"])), function (props) { return (props.grow ? 'flex-grow: 1; height: 100%;' : ''); }, function (_a) {
    var size = _a.size, theme = _a.theme;
    return "width: " + (size && theme._app.containerSizes[size] ? theme._app.containerSizes[size] : 'unset');
}, function (_a) {
    var theme = _a.theme, currentTheme = _a.currentTheme;
    return theme._themes[currentTheme || 'dark'].text;
}, function (_a) {
    var theme = _a.theme, currentTheme = _a.currentTheme;
    return theme._themes[currentTheme || 'dark'].bg;
}, function (_a) {
    var theme = _a.theme, currentTheme = _a.currentTheme;
    return theme._app.fontSizes[theme._themes[currentTheme || 'dark'].textSize];
});
var Message = styled(m.div)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  align-self: center;\n  margin: auto 0;\n"], ["\n  align-self: center;\n  margin: auto 0;\n"])));
var genericStateToggler = function (stateSetter, callback, nodeId, multi) {
    if (multi === void 0) { multi = false; }
    stateSetter(function (p) {
        var _p = p.slice();
        var toggledIndex = _p.findIndex(function (id) { return id === nodeId; });
        // in case of select, which might be single or multi
        // handle single select
        if (!multi) {
            var result = [nodeId];
            if (toggledIndex != -1) {
                result = [];
            }
            callback(result);
            return result;
        }
        // handle multi select (which is default for open/close toggle)
        if (toggledIndex !== -1) {
            _p = _p.filter(function (id) { return id !== nodeId; });
        }
        else {
            _p.push(nodeId);
        }
        callback(_p);
        return _p;
    });
};
var Tree = function (_a) {
    var _b = _a.nodes, nodes = _b === void 0 ? [] : _b, _c = _a.customTheme, customTheme = _c === void 0 ? {} : _c, _d = _a.theme, theme = _d === void 0 ? 'dark' : _d, _e = _a.size, size = _e === void 0 ? 'full' : _e, _f = _a.grow, grow = _f === void 0 ? false : _f, _g = _a.onSelect, onSelect = _g === void 0 ? function () { } : _g, _h = _a.onOpenClose, onOpenClose = _h === void 0 ? function () { } : _h, _j = _a.containerStyle, containerStyle = _j === void 0 ? {} : _j, _k = _a.showEmptyItems, showEmptyItems = _k === void 0 ? false : _k, _l = _a.noIcons, noIcons = _l === void 0 ? false : _l, _m = _a.children, children = _m === void 0 ? function () { } : _m, _o = _a.isLoading, isLoading = _o === void 0 ? false : _o, _q = _a.NodeRenderer, NodeRenderer = _q === void 0 ? null : _q, _r = _a.LeafRenderer, LeafRenderer = _r === void 0 ? null : _r, _s = _a.IconRenderer, IconRenderer = _s === void 0 ? null : _s, _t = _a.animations, animations = _t === void 0 ? false : _t, _u = _a.noDataString, noDataString = _u === void 0 ? null : _u, _v = _a.loadingString, loadingString = _v === void 0 ? null : _v, _w = _a.emptyItemsString, emptyItemsString = _w === void 0 ? null : _w;
    /**
     * We need to ensure that any changes to the content of the nodes list (create, delete)
     * re-renders the list and de-selects any selected nodes
     */
    var _x = __read(React.useState(nodes), 2), internalNodes = _x[0], setInternalNodes = _x[1];
    var _y = __read(React.useState([]), 2), selectedNodeIds = _y[0], setSelectedNodeIds = _y[1];
    var _z = __read(React.useState([]), 2), openNodeIds = _z[0], setOpenNodeIds = _z[1];
    var treeRef = React.useRef(null);
    // listen to changes on nodes
    React.useEffect(function () {
        setInternalNodes(nodes);
    }, [nodes]);
    /**
     * We should also be able to handle dynamic theme changes
     */
    var _theme = React.useMemo(function () {
        return {
            _themes: __assign(__assign({}, coreTheme._themes), customTheme),
            _app: __assign({}, coreTheme._app)
        };
    }, [customTheme]);
    /**
     * Handlers
     * We need a handler for:
     * 1. Handling a single select toggle
     * 2. Handling a ALL select toggle
     * 3. Toggling an open node
     * 4. Toggling all nodes open/closed
     */
    // 1. Handling a single select toggle
    var toggleNodeSelection = genericStateToggler.bind(null, setSelectedNodeIds, onSelect);
    // 2. Handling a ALL select toggle
    var toggleSelectAllNodes = function () {
        // TODO: make sure that this selects even leaf nodes
        if (selectedNodeIds.length === 0) {
            setSelectedNodeIds(function () {
                var selection = internalNodes
                    .map(function (el) {
                    return [el.id].concat(el.items ? el.items.map(function (i) { return i.id; }) : []);
                })
                    .flatMap(function (n) { return n; });
                onSelect(selection);
                return selection;
            });
            return true;
        }
        setSelectedNodeIds(function () {
            onSelect([]);
            return [];
        });
        return false;
    };
    // 3. Toggling an open node
    var toggleOpenCloseNode = genericStateToggler.bind(null, setOpenNodeIds, onOpenClose);
    // 4. Toggling all nodes open/closed
    var toggleOpenCloseAllNodes = function () {
        if (openNodeIds.length !== internalNodes.length) {
            setOpenNodeIds(function () {
                var openIds = internalNodes.map(function (el) { return el.id; });
                onOpenClose(openIds);
                return openIds;
            });
            return true;
        }
        setOpenNodeIds(function () {
            onOpenClose([]);
            return [];
        });
        return false;
    };
    /**
     * Finally we need to set up a listener to deselect any nodes if there is a click outside of the selected tree
     */
    React.useEffect(function () {
        var isOutsideClick = function (e) {
            var _a;
            if (!((_a = treeRef === null || treeRef === void 0 ? void 0 : treeRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                // must be outside of the tree
                onSelect([]);
                setSelectedNodeIds([]);
            }
        };
        if (!window)
            return;
        window.addEventListener('click', isOutsideClick);
        return function () {
            if (!window)
                return;
            window.removeEventListener('click', isOutsideClick);
        };
    }, []);
    var LoadingIcon = IconRenderer && typeof IconRenderer === 'function' ? (React.createElement(Icon, { size: "large" },
        React.createElement(IconRenderer, { label: "loader" }))) : (React.createElement(Icon, { size: "large", defaultIcon: true, initial: { rotate: 0 }, animate: { rotate: 360 }, transition: { repeat: Infinity, duration: 0.8 } },
        React.createElement(DefaultLoaderIcon, null)));
    var content = !isLoading ? (React.createElement(React.Fragment, null,
        typeof children === 'function' &&
            children({
                toggleNodeSelection: toggleNodeSelection,
                toggleSelectAllNodes: toggleSelectAllNodes,
                toggleOpenCloseNode: toggleOpenCloseNode,
                toggleOpenCloseAllNodes: toggleOpenCloseAllNodes,
                selectedNodeIds: selectedNodeIds,
                openNodeIds: openNodeIds
            }),
        !nodes.length && (React.createElement(Message, null,
            React.createElement("span", null, noDataString ? noDataString : 'No Data...'))),
        !!nodes.length && (React.createElement(Container, { selectedNodes: selectedNodeIds, openNodes: openNodeIds, didToggleSelect: toggleNodeSelection, didToggleOpen: toggleOpenCloseNode, parent: null, nodes: nodes, currentTheme: theme || 'dark', noIcons: noIcons, showEmptyItems: showEmptyItems, NodeRenderer: NodeRenderer, LeafRenderer: LeafRenderer, IconRenderer: IconRenderer, animations: animations, emptyItemsString: emptyItemsString })))) : (React.createElement(Message, null,
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', padding: '20px' } },
            React.createElement("span", { style: { paddingRight: '4px' } }, loadingString ? loadingString : 'Loading...'),
            !loadingString && LoadingIcon)));
    return (React.createElement(ThemeProvider, { theme: _theme },
        React.createElement(LazyMotion, { features: domAnimation },
            React.createElement(TreeBoundary, { ref: treeRef, grow: grow, currentTheme: theme || 'dark', size: size, style: __assign({}, containerStyle) }, content))));
};
export default Tree;
var templateObject_1, templateObject_2;
//# sourceMappingURL=index.js.map